import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { Coordinate, EulerOrder, isDefined, isNil, Quaternion, Vector3 } from '@trimble-gcs/common';
import { FeatureFlagService } from '@trimble-gcs/feature-flags';
import {
  catchError,
  combineLatest,
  concat,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  fromEvent,
  map,
  Observable,
  of,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import {
  Camera,
  CameraMode,
  ChangedViewerTool,
  File as ConnectViewerFile,
  IColor,
  ModelObjectIds,
  PanoramaMetadata,
  PointColorType,
  PointIcon,
  PointMarkup,
  PointPickedDetail,
  PointShape,
  ProjectionType,
  SectionBoxUpdate,
  ViewAction,
} from 'trimble-connect-workspace-api';
import { AppState } from '../app-state/app.state';
import {
  ClassificationScheme,
  DEFAULT_CLASSIFICATION_COLOR,
} from '../classification/classification-scheme.model';
import { ConnectWorkspace } from '../connect/connect.models';
import { ConnectService } from '../connect/connect.service';
import { Connect3dCamera } from '../connect/models/connect-3d-camera';
import { FeatureFlagKey } from '../feature-flags/feature-flag-key';
import { injectLogger } from '../logging/logger-injection';
import { DEFAULT_SCAN_3D_STYLE, Scan3dStyle } from '../scan-3d-panel/models/scan-3d-style';
import { Scan3dStyleRenderingMode } from '../scan-3d-panel/models/scan-3d-style-rendering-mode';
import { Scan3dPanelComponent } from '../scan-3d-panel/scan-3d-panel.component';
import { getElevationGradient } from '../scan-3d-panel/scan-3d-styling/scan-3d-elevation/scan-3d-elevation-utils';
import { CloseSortMenu, CloseTreeMenu, ElevationPicked } from '../scan-3d-panel/scan-3d.actions';
import { Scan3dService } from '../scan-3d-panel/scan-3d.service';
import { sortScandata } from '../scandata/scandata-sort';
import {
  PatchScandataModel,
  PatchScandataModels,
  SelectOnly,
  SetScandata,
  SetSortInfo,
} from '../scandata/scandata.actions';
import {
  ScandataDisplayStatus,
  ScandataLoadStatus,
  ScandataModel,
} from '../scandata/scandata.models';
import { ScandataService } from '../scandata/scandata.service';
import { ScandataState } from '../scandata/scandata.state';
import {
  calculateStationHeadingOffset,
  getStationTransformHeading,
  StationTransform,
} from '../station/station-transform.models';
import { ClearCurrentStation, SetCurrentStation } from '../station/station.actions';
import {
  CurrentStation,
  Station,
  StationDisplayStatus,
  StationStatus,
} from '../station/station.models';
import { StationState } from '../station/station.state';
import { TilesetFormat, TilesetStatus } from '../tileset/tileset.models';
import { Role } from '../user/user.models';
import { UserState } from '../user/user.state';
import { colorHexToArray } from '../utils/color-converter';
import { SetCameraProjection } from './camera/camera.action';
import { CameraProjection } from './camera/camera.models';
import { CameraState } from './camera/camera.state';
import { sectionBoxToClipBox } from './clip-box/clip-box-conversion';
import { ClearClipBox, SetClipBox } from './clip-box/clip-box.actions';
import { Host3dFitToViewService } from './host-3d-fit-to-view.service';
import { Host3dViewService } from './host-3d-view.service';
import { ClearHost3dScandata, RotatePanoramas } from './host-3d.actions';
import { Host3dService } from './host-3d.service';
import { Host3dState } from './host-3d.state';
import { host3dTranslations } from './host-3d.translations';

export interface PointIconExt extends PointIcon {
  // Not defined in current workspace api, but present in event.
  // Connect uses an InternalPointIcon which extends the interface in the Workspace API.
  // This interface is however not exported.
  // This was communicated to Connect for a solution.
  providedId: number;
}

@UntilDestroy()
@Component({
  selector: 'sd-host-3d',
  imports: [Scan3dPanelComponent],
  templateUrl: './host-3d.component.html',
  styleUrls: ['./host-3d.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Host3dComponent implements OnInit {
  private readonly actions$ = inject(Actions);
  private readonly featureFlagService = inject(FeatureFlagService<FeatureFlagKey>);
  private readonly host3dFitToViewService = inject(Host3dFitToViewService);

  private scandata$ = this.store.select(ScandataState.scandata);
  private currentStation$ = this.store.select(StationState.currentStation);
  private cameraProjection$ = this.store.select(CameraState.projection);

  readonly globalStyle = toSignal(this.scan3dService.getGlobalStyle());
  readonly elevationPicking = signal<boolean>(false);

  private globalStyle$ = toObservable(this.globalStyle).pipe(filter(isDefined));

  private elevationRenderingMode$ = combineLatest([this.scandata$, this.globalStyle$]).pipe(
    map(([scandata, globalStyle]) => {
      return (
        globalStyle.renderingMode === Scan3dStyleRenderingMode.Elevation ||
        scandata.some((s) => s.scan3dStyle?.renderingMode === Scan3dStyleRenderingMode.Elevation)
      );
    }),
    distinctUntilChanged(),
  );

  readonly hasClearanceWebGPUFeature = toSignal(
    this.featureFlagService.hasFeature$('3dClearanceWebGPU'),
  );

  private readonly hasStationIconCullingFeature = toSignal(
    this.featureFlagService.hasFeature$('3d_style_stationIconCulling'),
    { initialValue: false },
  );

  private workspace!: ConnectWorkspace;
  private shouldRequestFocus = false;
  private selectedScans?: ScandataModel[];
  private fitToViewEnabled = true;
  private connnect3dCamera: Connect3dCamera = {};

  private markupToMeter = 1000;
  private elevationPointMarkup?: PointMarkup;

  private logger = injectLogger('Host3dComponent');

  private readonly translations = host3dTranslations();

  constructor(
    private connectService: ConnectService,
    private scandataService: ScandataService,
    private host3dService: Host3dService,
    private scan3dService: Scan3dService,
    private host3dViewService: Host3dViewService,
    private store: Store,
  ) {
    // Set default 3D sort order
    this.store.dispatch(new SetSortInfo({ sortBy: 'name', sortDirection: 'asc' }));

    const scandata = this.store.selectSnapshot(Host3dState.scandata);
    if (scandata) {
      this.store.dispatch(new SetScandata(scandata));
      this.store.dispatch(new ClearHost3dScandata());
      this.shouldRequestFocus = true;
      this.selectedScans = scandata.filter((x) => x.selected);
    }

    this.selectScandataFromQueryParams();
    this.createElevationPickingEffect();
  }

  async ngOnInit(): Promise<void> {
    this.workspace = await this.connectService.getWorkspace();

    await this.requestFocus();

    if (isDefined(this.selectedScans) && this.selectedScans.length > 0) {
      this.showScans(this.selectedScans);
    }

    this.subscribeToHideScans();
    this.subscribeToDisplayScans();
    this.subscribeToStyleScans();
    this.subscribeToStationIcons();
    this.subscribeToCurrentStation();
    this.subscribeToIconPick();
    this.subscribeToSelectionChangedForClearance();
    this.subscribeToEventsToCloseMenu();
    this.subscribeToCloseAndReset();
    this.subscribeToViewEvent();
    this.subscribeToWindowBlur();
    this.subscribeToRotatePanoramas();
    this.subscribeToElevationPicked();
    this.subscribeToElevationToolChanged();
    this.subscribeToRenderElevationMarkup();
    this.subscribeToSectionBoxChanged();
    this.displayViewAfterRefresh();

    await this.setInitialCameraState();
    this.updateCameraProjectionState();
    this.exitStationForCameraPosition();
    this.exitStationForOrthographic();
  }

  onElevationPickingChange(picking: boolean) {
    this.elevationPicking.set(picking);
  }

  private subscribeToHideScans() {
    this.scandata$
      .pipe(
        map((scans) => scans.filter((x) => x.displayStatus === ScandataDisplayStatus.AwaitingHide)),
        distinctUntilChanged((prev, curr) => this.scansChanged(prev, curr)),
        filter((scans) => scans.length > 0),
        untilDestroyed(this),
      )
      .subscribe((scans) => this.unloadModels(scans));
  }

  private subscribeToDisplayScans() {
    this.scandata$
      .pipe(
        map((scans) =>
          scans.filter(
            (x) =>
              x.showInScene &&
              x.loadStatus === ScandataLoadStatus.Loaded &&
              x.displayStatus === ScandataDisplayStatus.Hidden,
          ),
        ),
        distinctUntilChanged((prev, curr) => this.scansChanged(prev, curr)),
        filter((scans) => scans.length > 0),
        withLatestFrom(this.currentStation$),
        untilDestroyed(this),
      )
      .subscribe(([scandata, currentStation]) => this.displayScans(scandata, currentStation));
  }

  private subscribeToStyleScans() {
    combineLatest([this.scandata$, this.globalStyle$])
      .pipe(
        switchMap(([scandata, globalStyle]) => this.updateModelStyling(scandata, globalStyle)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private async displayScans(scans: ScandataModel[], currentStation?: CurrentStation) {
    await this.loadModels(scans);

    // Restoring a Connect View sets the camera so don't dispatch fitToView.
    if (!currentStation && this.fitToViewEnabled) {
      this.host3dFitToViewService.fitToView(scans);
    } else if (!this.fitToViewEnabled) {
      this.fitToViewEnabled = true;
    }
  }

  private async loadModels(models: ScandataModel[]) {
    this.scandataService.setDisplayStatus(models, ScandataDisplayStatus.AwaitingDisplay);

    const patchModels: Partial<ScandataModel>[] = [];
    for (const model of models) {
      const displayStatus = (await this.loadTilesetsForModel(model))
        ? ScandataDisplayStatus.Displayed
        : ScandataDisplayStatus.DisplayError;

      patchModels.push({
        id: model.id,
        displayStatus,
      });
    }

    this.store.dispatch(new PatchScandataModels(patchModels));

    return patchModels;
  }

  private async unloadModels(models: ScandataModel[]) {
    try {
      const pointClouds = await this.workspace.api.viewer.getPointClouds();
      const foundModels = models.filter((model) => pointClouds.some((x) => x.id === model.web3dId));

      const removed = foundModels.filter(
        async (model) => await this.removeModelFromView(model.web3dId, model.id),
      );

      this.scandataService.setDisplayStatus(removed, ScandataDisplayStatus.Hidden);
    } catch (error: any) {
      this.logger.error(`Unloading models failed`, {}, error);
    }
  }

  private async loadTilesetsForModel(model: ScandataModel): Promise<boolean> {
    const readyPotreeTileset = model.tilesets?.find(
      (x) => x.status === TilesetStatus.Ready && x.format === TilesetFormat.Potree,
    );

    if (isNil(readyPotreeTileset) || isNil(readyPotreeTileset.rootUrl)) return false;

    try {
      return await this.workspace.api.viewer.addPointCloud({
        id: model.web3dId,
        url: readyPotreeTileset.rootUrl,
      });
    } catch (error: any) {
      this.logger.error(`Point cloud ${model.id} failed to load`, {}, error);
      return false;
    }
  }

  private subscribeToCurrentStation() {
    const waitForScans$ = () =>
      this.scandata$.pipe(
        filter((scans) => scans.length > 0),
        take(1),
      );

    this.currentStation$
      .pipe(
        pairwise(),
        switchMap(([previous, current]) =>
          waitForScans$().pipe(
            map((scans) => ({
              previous,
              current,
              scans,
            })),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe(async ({ previous, current, scans }) => {
        if (previous && (isNil(current) || previous.station.id !== current.station.id)) {
          const cameraMode = this.connnect3dCamera.cameraMode ?? CameraMode.Rotate;
          try {
            await this.workspace.api.viewer.setCameraMode(cameraMode);
          } catch (error: any) {
            this.logger.error(`Failed to set camera mode ${cameraMode}`, {}, error);
          }

          await this.enablePickingTool();
          await this.unloadStation(previous.station);
        }

        if (previous && !current) {
          await this.setCameraAfterStationExit(previous.station);
        }

        if (current) {
          const currentStationScan = scans.find((scan) => scan.id === current.station.pointcloudId);

          await this.loadCurrentStation(current, currentStationScan?.stationTransform);
        }
      });
  }

  private async loadCurrentStation(current: CurrentStation, stationTransform?: StationTransform) {
    if (current.displayStatus === StationDisplayStatus.AwaitingDisplay) {
      const added = await this.addStationToView(current.station, stationTransform);
      const cameraSet = added && (await this.setCameraForStation(current.station));
      const loaded = added && cameraSet;

      if (added && !cameraSet)
        await this.removeModelFromView(current.station.web3dId, current.station.id);

      this.store.dispatch(
        new SetCurrentStation({
          station: current.station,
          displayStatus: loaded
            ? StationDisplayStatus.Displayed
            : StationDisplayStatus.DisplayError,
        }),
      );
    }
  }

  private async addStationToView(station: Station, stationTransform?: StationTransform) {
    // Returning true here for location only stations
    if (isNil(station.tilesetInfo?.tilesetUrl)) return true;

    // TODO: Imaging Service tilesets are not yet supported by Connect
    if (station.tilesetInfo.tileTemplate.startsWith('http')) return false;

    const urlParts = station.tilesetInfo.tilesetUrl.split('?');
    const tilesUrl = urlParts[0] + '/' + station.tilesetInfo.tileTemplate + '?' + urlParts[1];

    // Legacy db entries - Previously tileSize was hard-coded as 512
    const tileHeight = station.tilesetInfo.tileHeight > 0 ? station.tilesetInfo.tileHeight : 512;

    const quaternion = this.getStationQuaternion(station, stationTransform);

    const metadata: PanoramaMetadata = {
      id: station.web3dId,
      quaternion,
      tilesUrl: tilesUrl,
      tileHeight: tileHeight,
      isLowerLeftOrigin: false,
      maxWidth: station.tilesetInfo.resizedWidth,
      maxHeight: station.tilesetInfo.resizedHeight,
      sphereRadius: 5,
      position: { x: station.position.x, y: station.position.y, z: station.position.z },
      fovTop: 90,
      fovBottom: -90,
      fovLeft: -180,
      fovRight: 180,
      minLodLevel: 3,
      lodAdjustment: 1,
    };

    return await this.workspace.api.viewer.addPanorama(metadata).catch((err) => {
      this.logger.error(`Adding station ${station.id} failed`, {}, err);
      return false;
    });
  }

  private getStationQuaternion(
    station: Station,
    stationTransform: StationTransform | undefined,
  ): Quaternion {
    if (isNil(station.orientation) && station.quaternion) {
      return this.applyHeadingToQuaternion(station.quaternion, stationTransform);
    }

    return this.convertLegacyEulerToQuaternion(station, stationTransform);
  }

  private applyHeadingToQuaternion(
    q: { x: number; y: number; z: number; w: number },
    stationTransform: StationTransform | undefined,
  ): Quaternion {
    const headingOffsetDegrees = calculateStationHeadingOffset(stationTransform);

    if (headingOffsetDegrees === 0) {
      return new Quaternion(q.x, q.y, q.z, q.w);
    }

    // Un-permute to reverse the LodAnalyzer (x,y,z,w)→(z,w,x,y) pre-permutation
    const unpermuted = new Quaternion(q.z, q.w, q.x, q.y);

    // Heading offset as a Z-axis rotation (Z is vertical in the post-conversion frame)
    const halfAngle = (headingOffsetDegrees * Math.PI) / 360;
    const headingQuat = new Quaternion(0, 0, Math.sin(halfAngle), Math.cos(halfAngle));
    const rotated = Quaternion.multiply(headingQuat, unpermuted);

    // Re-permute for LodAnalyzer
    return new Quaternion(rotated.z, rotated.w, rotated.x, rotated.y);
  }

  private convertLegacyEulerToQuaternion(
    station: Station,
    stationTransform: StationTransform | undefined,
  ): Quaternion {
    const DEG2RAD = Math.PI / 180;

    const headingOffsetDegrees = calculateStationHeadingOffset(stationTransform);
    const heading = (station.orientation?.yaw ?? 0) + headingOffsetDegrees;
    const pitch = station.orientation?.pitch ?? 0;
    const roll = station.orientation?.roll ?? 0;
    const order = (station.orientation?.rotationOrder ?? 'XYZ') as EulerOrder;

    const eulerQuat = Quaternion.fromEulerAngles(
      roll * DEG2RAD,
      pitch * DEG2RAD,
      heading * DEG2RAD,
      order,
    );

    // Y-up to Z-up conversion (90° X-axis rotation) — matches legacy euler path
    const yUpToZUp = new Quaternion(Math.SQRT1_2, 0, 0, Math.SQRT1_2);
    const result = Quaternion.multiply(eulerQuat, yUpToZUp);

    // Inverse permutation to cancel LodAnalyzer's (x,y,z,w) → (z,w,x,y)
    return new Quaternion(result.z, result.w, result.x, result.y);
  }

  private async removeModelFromView(web3dId: string, objectId: string) {
    try {
      return await this.workspace.api.viewer.removeModel(web3dId);
    } catch (error: any) {
      this.logger.error(`Removing web3d model ${objectId} failed`, {}, error);
      return false;
    }
  }

  private async setCameraAfterStationExit(station: Station) {
    try {
      const camera = await this.workspace.api.viewer.getCamera();
      camera.fieldOfView = this.connnect3dCamera.fieldOfView;

      this.connnect3dCamera = {};

      await this.connectService.setCamera({ ...camera });
    } catch (error: any) {
      this.logger.error(`Setting camera after station ${station.id} exit failed`, {}, error);
    }
  }

  private async setCameraForStation(station: Station) {
    try {
      const camera = await this.workspace.api.viewer.getCamera();
      const cameraMode = await this.workspace.api.viewer.getCameraMode();
      const fieldOfView = this.connnect3dCamera.fieldOfView ?? camera.fieldOfView;

      this.connnect3dCamera = { ...camera, fieldOfView, cameraMode };

      await this.connectService.setCamera({
        ...camera,
        position: station.position,
      });

      return await this.workspace.api.viewer.setCameraMode(CameraMode.Panorama);
    } catch (error: any) {
      this.logger.error(`Failed to set camera for station ${station.id}`, {}, error);
      return false;
    } finally {
      await this.enablePickingTool();
    }
  }

  private async unloadStation(station: Station) {
    // Connect currently bundles stations and point clouds in the same result

    try {
      const pointClouds = await this.workspace.api.viewer.getPointClouds();

      if (pointClouds.find((x) => x.id === station.web3dId)) {
        await this.removeModelFromView(station.web3dId, station.id);
      }
    } catch (error: any) {
      this.logger.error(`Unloading station ${station.id} failed`, {}, error);
    }
  }

  private async reloadStation(station: Station, stationTransform?: StationTransform) {
    await this.unloadStation(station);
    await this.addStationToView(station, stationTransform);
  }

  private getStationsForModels(models: ScandataModel[]): Station[] {
    return models.flatMap((model) => model.stations ?? []);
  }

  private async addStationIcon(station: Station) {
    try {
      const icon = this.mapStationToPointIcon(station);
      await this.workspace.api.viewer.addIcon(icon);
      return true;
    } catch (error: any) {
      this.logger.error(`Add icon for station ${station.id} failed`, {}, error);
      return false;
    }
  }

  private async removeStationIcon(station: Station) {
    try {
      const icon = this.mapStationToPointIcon(station);
      await this.workspace.api.viewer.removeIcon(icon);
      return true;
    } catch (error: any) {
      this.logger.error(`Remove icon for station ${station.id} failed`, {}, error);
      return false;
    }
  }

  private subscribeToStationIcons() {
    const cameraPosition$ = this.observeCameraChangeEvent().pipe(
      map((camera) => camera.position),
      filter((position): position is Vector3 => isDefined(position)),
      map((position) => Vector3.fromCoordinate(position)),
      startWith<Vector3 | undefined>(undefined),
      shareReplay(1),
    );

    combineLatest([
      this.scandata$,
      this.currentStation$,
      this.cameraProjection$,
      this.globalStyle$,
      cameraPosition$,
    ])
      .pipe(
        map(([scans, currentStation, cameraProjection, globalStyle, cameraPosition]) => ({
          scans,
          desired: this.computeDesiredStationIconIds(
            scans,
            currentStation?.station,
            cameraProjection,
            cameraPosition,
            globalStyle,
          ),
        })),
        distinctUntilChanged((prev, curr) => this.iconIdSetsEqual(prev.desired, curr.desired)),
        switchMap(({ scans, desired }) => this.applyIconDiff(scans, desired)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private async applyIconDiff(scans: ScandataModel[], desired: Set<number>): Promise<void> {
    const viewerIcons = await this.getViewerIcons();

    if (isNil(viewerIcons)) return;

    const managedStations = this.getStationsForModels(scans);
    const stationByIconId = new Map(managedStations.map((s) => [s.iconId, s]));
    const managedIconIds = new Set(stationByIconId.keys());

    const current = new Set(viewerIcons.map((i) => i.id).filter((id) => managedIconIds.has(id)));

    // web3d-plugin-icons cannot handle parallel addIcon/removeIcon calls
    for (const iconId of current) {
      if (desired.has(iconId)) continue;
      const station = stationByIconId.get(iconId);
      if (isDefined(station)) await this.removeStationIcon(station);
    }

    for (const iconId of desired) {
      if (current.has(iconId)) continue;
      const station = stationByIconId.get(iconId);
      if (isDefined(station)) await this.addStationIcon(station);
    }
  }

  private iconIdSetsEqual(a: Set<number>, b: Set<number>): boolean {
    if (a.size !== b.size) return false;
    for (const id of a) {
      if (!b.has(id)) return false;
    }
    return true;
  }

  private computeDesiredStationIconIds(
    scans: ScandataModel[],
    currentStation: Station | undefined,
    cameraProjection: CameraProjection | undefined,
    cameraPosition: Vector3 | undefined,
    globalStyle: Scan3dStyle,
  ): Set<number> {
    const desired = new Set<number>();

    if (cameraProjection !== CameraProjection.Perspective) return desired;

    const cullingDistanceByScanId = this.getCullingDistanceByScanId(scans, globalStyle);
    const minStationIconsByScanId = this.getMinStationIconsByScanId(scans, globalStyle);

    for (const scan of scans) {
      if (scan.displayStatus !== ScandataDisplayStatus.Displayed) continue;

      const maxDistance =
        cullingDistanceByScanId.get(scan.id) ?? globalStyle.stationIconCullingDistance;
      const minIcons = minStationIconsByScanId.get(scan.id) ?? globalStyle.minStationIconsPerScan;

      const candidates = (scan.stations ?? []).filter(
        (station) => station.status === StationStatus.Ready && station.id !== currentStation?.id,
      );

      const selected = this.selectStationsToDisplay(
        candidates,
        cameraPosition,
        maxDistance,
        minIcons,
      );

      for (const station of selected) {
        desired.add(station.iconId);
      }
    }

    return desired;
  }

  // Picks which stations should be visible for a single scan: all stations
  // within maxDistance, plus the minIcons-closest stations regardless of
  // distance, so there is always some spatial awareness per scan.
  private selectStationsToDisplay(
    stations: Station[],
    cameraPosition: Vector3 | undefined,
    maxDistance: number,
    minIcons: number,
  ): Station[] {
    if (stations.length === 0) return [];
    if (!this.hasStationIconCullingFeature()) return stations;
    if (isNil(cameraPosition)) return stations;

    const withDistance = stations
      .map((station) => ({
        station,
        distance: cameraPosition.subtract(Vector3.fromCoordinate(station.position)).magnitude(),
      }))
      .sort((a, b) => a.distance - b.distance);

    return withDistance
      .filter(({ distance }, index) => index < minIcons || distance <= maxDistance)
      .map(({ station }) => station);
  }

  private getCullingDistanceByScanId(
    scans: ScandataModel[],
    globalStyle: Scan3dStyle,
  ): Map<string, number> {
    const distanceByScanId = new Map<string, number>();
    for (const scan of scans) {
      distanceByScanId.set(
        scan.id,
        scan.scan3dStyle?.stationIconCullingDistance ??
          globalStyle.stationIconCullingDistance ??
          DEFAULT_SCAN_3D_STYLE.stationIconCullingDistance,
      );
    }

    return distanceByScanId;
  }

  private getMinStationIconsByScanId(
    scans: ScandataModel[],
    globalStyle: Scan3dStyle,
  ): Map<string, number> {
    const minByScanId = new Map<string, number>();
    for (const scan of scans) {
      minByScanId.set(
        scan.id,
        scan.scan3dStyle?.minStationIconsPerScan ??
          globalStyle.minStationIconsPerScan ??
          DEFAULT_SCAN_3D_STYLE.minStationIconsPerScan,
      );
    }

    return minByScanId;
  }

  private async getViewerIcons(): Promise<PointIcon[] | undefined> {
    try {
      return await this.workspace.api.viewer.getIcon();
    } catch (error: any) {
      this.logger.error(`Failed to get station icons from viewer`, {}, error);
      return undefined;
    }
  }

  private mapStationToPointIcon(station: Station): PointIcon {
    const baseUrl = this.store.selectSnapshot(AppState.settings).baseUrl;

    return {
      id: station.iconId,
      iconPath: `${baseUrl}/assets/station.png`,
      position: { x: station.position.x, y: station.position.y, z: station.position.z },
      size: 40,
    };
  }

  private subscribeToSelectionChangedForClearance() {
    this.workspace.event$
      .pipe(
        filter((event) => event.id === 'viewer.onSelectionChanged'),
        map((event) => event.data as ModelObjectIds[]),
        withLatestFrom(this.scandata$, this.globalStyle$),
        filter(
          ([, scandata, globalStyle]) =>
            scandata.some(
              (x) => x.scan3dStyle?.renderingMode === Scan3dStyleRenderingMode.Clearance,
            ) || globalStyle.renderingMode === Scan3dStyleRenderingMode.Clearance,
        ),
        untilDestroyed(this),
      )
      .subscribe(async ([objectIds, scandata, globalStyle]) => {
        const clearanceScans = scandata.filter(
          (x) =>
            x.scan3dStyle?.renderingMode === Scan3dStyleRenderingMode.Clearance ||
            globalStyle.renderingMode === Scan3dStyleRenderingMode.Clearance,
        );

        if (objectIds.length === 0) {
          clearanceScans.forEach((scan) => {
            this.workspace.api.viewer.removePointCloudClearance(scan.web3dId);
          });

          return;
        }

        // Currently only one model is supported for clearance
        const trimbimModelId = objectIds[0].modelId;
        const objectRuntimeIds = objectIds[0].objectRuntimeIds ?? [];

        const trimbimFile = await this.workspace.api.viewer.getLoadedModel(trimbimModelId);

        const promises = clearanceScans.map(async (scan) => {
          this.setClearance(scan.web3dId, trimbimFile, objectRuntimeIds);
        });

        await Promise.all(promises);
      });
  }

  private subscribeToIconPick() {
    const stations$ = this.scandata$.pipe(map((models) => this.getStationsForModels(models)));

    this.workspace.event$
      .pipe(
        filter((event) => event.id === 'viewer.onIconPicked'),
        map((event) => event.data as PointIconExt[]),
        filter((icons) => icons && icons.length > 0),
        withLatestFrom(this.currentStation$),
        filter(([icons, currentStation]) => currentStation?.station.iconId !== icons[0].providedId),
        withLatestFrom(stations$),
        map(([[icons], stations]) => stations.find((s) => s.iconId === icons[0].providedId)),
        filter((station): station is Station => isDefined(station)),
        switchMap((station) => this.host3dService.setCurrentStation(station)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private subscribeToEventsToCloseMenu() {
    this.workspace.event$
      .pipe(
        filter(
          (event) =>
            event.id === 'viewer.onPicked' ||
            event.id === 'viewer.onIconPicked' ||
            event.id === 'viewer.onCameraChanged',
        ),
        untilDestroyed(this),
      )
      .subscribe(() => this.store.dispatch([new CloseTreeMenu(), new CloseSortMenu()]));
  }

  private subscribeToCloseAndReset() {
    this.workspace.event$
      .pipe(
        filter((event) => event.id === 'extension.closing' || event.id === 'viewer.onModelReset'),
        withLatestFrom(this.scandata$, this.currentStation$),
        untilDestroyed(this),
      )
      .subscribe(([, scandata, currentStation]) => {
        const actions = [];

        const modelsToHide = scandata.filter(
          (model) =>
            model.displayStatus &&
            ![ScandataDisplayStatus.Hidden, ScandataDisplayStatus.AwaitingHide].includes(
              model.displayStatus,
            ),
        );

        if (modelsToHide.length > 0) {
          actions.push(
            new PatchScandataModels(
              modelsToHide.map((x) => ({
                id: x.id,
                displayStatus: ScandataDisplayStatus.AwaitingHide,
                showInScene: false,
              })),
            ),
          );
        }

        if (isDefined(currentStation)) {
          actions.push(new ClearCurrentStation());
        }

        if (actions.length > 0) {
          this.store.dispatch(actions);
        }
      });
  }

  private async updateModelStyling(scandata: ScandataModel[], globalStyle: Scan3dStyle) {
    const models = scandata.filter((s) => s.displayStatus === ScandataDisplayStatus.Displayed);
    const trimbimFile = await this.getTrimbimFileForClearance();

    models.forEach(async (model) => {
      const style = model.scan3dStyle ?? globalStyle;
      const { colors, visibleArray } = this.getClassificationSettings(
        style.classificationSchemes,
        model,
      );

      if (style.renderingMode !== Scan3dStyleRenderingMode.Clearance) {
        this.workspace.api.viewer.removePointCloudClearance(model.web3dId);
      }

      if (style.renderingMode === Scan3dStyleRenderingMode.Clearance && isDefined(trimbimFile)) {
        const modelObjects = await this.workspace.api.viewer.getObjects({ selected: true });

        // To get the objects, the id is used and not the runtimeId
        const trimbimModelObjects = modelObjects.filter((x) => x.modelId === trimbimFile.id);

        const objectRuntimeIds = trimbimModelObjects.flatMap((x) => x.objects).map((o) => o.id);

        this.setClearance(model.web3dId, trimbimFile, objectRuntimeIds);
      }

      // An extra band is needed to render the out of tolerance color
      const toleranceWithExtraBand =
        (style.elevationTolerance / style.elevationBandCount) * (style.elevationBandCount + 1);

      await this.workspace.api.viewer
        .setPointCloudSettings(
          {
            classificationColors: colors,
            classificationVisibility: visibleArray,
            pointColorBy: this.getPointColorByForStyle(style),
            pointSize: style.pointSize,
            densityBias: style.pointDensity,
            pointBudget: style.pointBudget,
            pointShape: PointShape.PARABOLOID,
            sizeAttenuation: false,
            shading: style.showEyeDomeLighting ? 'EYE_DOME_LIGHTING' : 'DEFAULT',
            edlRadius: 1.4,
            edlStrength: 0.7,
            intensityGradient: this.getIntensityGradient(style),
            clearanceDefaultTransparent: style.clearanceDefaultTransparent,
            clearanceDefaultColor: this.getClearanceDefaultColor(style),
            clearanceBandBoundaries: this.getClearanceBandBoundaries(style),
            clearanceBandColors: this.getClearanceBandColors(style),
            elevationTop: style.elevationReference + style.elevationOffset + toleranceWithExtraBand,
            elevationBottom:
              style.elevationReference + style.elevationOffset - toleranceWithExtraBand,
            elevationGradient: this.getElevationGradient(style),
          },
          [model.web3dId],
        )
        .catch((err) => {
          this.logger.error(`Set point cloud settings ${model.id} failed`, {}, err);
          return false;
        });
    });
  }

  private getPointColorByForStyle(style: Scan3dStyle) {
    if (style.renderingMode === Scan3dStyleRenderingMode.Classification)
      return style.blendIntensity
        ? PointColorType.INTENSITY_CLASSIFICATION
        : PointColorType.CLASSIFICATION;

    if (style.renderingMode === Scan3dStyleRenderingMode.Rgb && style.blendIntensity)
      return PointColorType.INTENSITY;
    if (style.renderingMode === Scan3dStyleRenderingMode.Clearance) return PointColorType.CLEARANCE;
    if (style.renderingMode === Scan3dStyleRenderingMode.Elevation) return PointColorType.ELEVATION;
    return PointColorType.RGB;
  }

  private getIntensityGradient(style: Scan3dStyle): [number, IColor][] | undefined {
    if (!style.blendIntensity) return undefined;

    const startColor = colorHexToArray(style.intensityStartColor) as number[];
    const endColor = colorHexToArray(style.intensityEndColor) as number[];

    return [
      [0, { r: startColor[0], g: startColor[1], b: startColor[2] }],
      [style.intensityOffset, { r: endColor[0], g: endColor[1], b: endColor[2] }],
    ];
  }

  private getClearanceDefaultColor(style: Scan3dStyle): IColor | undefined {
    const color = colorHexToArray(style.clearanceDefaultColor);
    if (isNil(color)) return undefined;
    return { r: color[0], g: color[1], b: color[2] };
  }

  private getClearanceBandBoundaries(style: Scan3dStyle): number[] {
    return style.clearanceBands.map((b) => b.start);
  }

  private getClearanceBandColors(style: Scan3dStyle): IColor[] {
    return style.clearanceBands.slice(0, style.clearanceBands.length - 1).map((b) => {
      const colorArray = colorHexToArray(b.color);

      return isDefined(colorArray)
        ? { r: colorArray[0], g: colorArray[1], b: colorArray[2] }
        : { r: 0, g: 0, b: 0 };
    });
  }

  private getElevationGradient(style: Scan3dStyle): [number, IColor][] | undefined {
    if (style.renderingMode !== Scan3dStyleRenderingMode.Elevation) return undefined;

    // An extra band is needed to render the out of tolerance color
    const bandCount = style.elevationBandCount + 1;

    return getElevationGradient(bandCount);
  }

  private getClassificationSettings(schemes: ClassificationScheme[], scan: ScandataModel) {
    const rgbHexArray = Array<string>(256).fill(DEFAULT_CLASSIFICATION_COLOR);
    const visibleArray = Array<boolean>(256).fill(true);

    schemes.forEach((scheme) => {
      rgbHexArray[scheme.id] = scheme.rgba;
      visibleArray[scheme.id] = scheme.visible;
    });

    scan.scan3dStyle?.classificationSchemes.forEach((scheme) => {
      rgbHexArray[scheme.id] = scheme.rgba;
      visibleArray[scheme.id] = scheme.visible;
    });

    const colors: IColor[] = rgbHexArray
      .map((rgb) => colorHexToArray(rgb) ?? [0, 0, 0])
      .map((s) => ({ r: s[0], g: s[1], b: s[2] }));

    return { colors, visibleArray };
  }

  private subscribeToViewEvent() {
    this.workspace.event$
      .pipe(
        filter((event) => event.id === 'view.onViewAction'),
        map((event) => event.data as ViewAction),
        switchMap((viewAction) =>
          this.getViewAction$(viewAction).pipe(
            take(1),
            catchError((err) => {
              this.logger.error(`View action '${viewAction.action}' failed`, {}, err);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  // View errors are caught to keep subscription alive
  private getViewAction$(viewAction: ViewAction): Observable<unknown> {
    switch (viewAction.action) {
      case 'created':
        return this.host3dViewService.createView(viewAction.view);
      case 'updated':
        return this.host3dViewService.updateView(viewAction.view);
      case 'removed':
        return this.host3dViewService.removeView(viewAction.view);
      case 'set': {
        this.fitToViewEnabled = false;
        return this.host3dViewService.setView(viewAction.view);
      }
    }
  }

  private subscribeToWindowBlur() {
    fromEvent(window, 'blur')
      .pipe(untilDestroyed(this))
      .subscribe(() => this.store.dispatch([new CloseTreeMenu(), new CloseSortMenu()]));
  }

  private subscribeToSectionBoxChanged() {
    this.workspace.event$
      .pipe(
        filter((event) => event.id === 'viewer.onSectionBoxChanged'),
        map((event) => event.data as SectionBoxUpdate),
        untilDestroyed(this),
      )
      .subscribe((update) => {
        if (update.action === 'removed') {
          this.store.dispatch(new ClearClipBox());
          return;
        }

        const clipBox = sectionBoxToClipBox(update.sectionBox);
        this.store.dispatch(new SetClipBox(clipBox));
      });
  }

  private scansChanged(prev: ScandataModel[], curr: ScandataModel[]) {
    return (
      isDefined(prev) &&
      prev.every((x) => isDefined(curr.find((c) => c.id === x.id))) &&
      curr.every((x) => isDefined(prev.find((p) => p.id === x.id)))
    );
  }

  private async displayViewAfterRefresh() {
    const currentView = await this.workspace.api.view.getCurrentView();
    if (isNil(currentView)) return;

    this.fitToViewEnabled = false;

    this.scandata$
      .pipe(
        filter((scans) => scans.length > 0),
        take(1), // setView updates scandata state which causes circular emissions
        switchMap(() => this.host3dViewService.setView(currentView)),
      )
      .subscribe({
        error: (err) => this.logger.error(`Load View after refresh failed`, {}, err),
      });
  }

  private async setInitialCameraState() {
    from(this.workspace.api.viewer.getCamera())
      .pipe(
        filter(isDefined),
        switchMap((camera) =>
          this.store.dispatch(
            new SetCameraProjection(this.mapCameraProjectionType(camera.projectionType)),
          ),
        ),
      )
      .subscribe({
        error: (err) => this.logger.error(`Set inital camera state failed`, {}, err),
      });
  }

  private updateCameraProjectionState() {
    this.observeCameraChangeEvent()
      .pipe(
        switchMap((camera) =>
          this.store.dispatch(
            new SetCameraProjection(this.mapCameraProjectionType(camera.projectionType)),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private exitStationForCameraPosition() {
    // Changing to a different Connect camera view point should exit the station
    this.observeCameraChangeEvent()
      .pipe(
        map((camera) => camera.position),
        filter((position): position is Vector3 => isDefined(position)),
        withLatestFrom(this.currentStation$),
        filter(
          ([, currentStation]) =>
            isDefined(currentStation) &&
            currentStation.displayStatus === StationDisplayStatus.Displayed,
        ),
        map(([position, currentStation]) => [
          position,
          currentStation?.station.position as Coordinate,
        ]),
        map((coordinates) => coordinates.map((c) => Vector3.fromCoordinate(c))),
        filter(([cameraPosition, stationPosition]) => !cameraPosition.match(stationPosition, 1e-6)),
        switchMap(() => this.store.dispatch(new ClearCurrentStation())),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private exitStationForOrthographic() {
    this.observeCameraChangeEvent()
      .pipe(
        map((camera) => this.mapCameraProjectionType(camera.projectionType)),
        withLatestFrom(this.currentStation$),
        filter(
          ([projection, currentStation]) =>
            projection === CameraProjection.Orthographic && isDefined(currentStation),
        ),
        switchMap(() => this.store.dispatch(new ClearCurrentStation())),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private observeCameraChangeEvent() {
    return this.workspace.event$.pipe(
      filter((event) => event.id === 'viewer.onCameraChanged'),
      map((event) => event.data as Camera),
    );
  }

  private mapCameraProjectionType(projectionType?: ProjectionType) {
    switch (projectionType) {
      case 'ortho':
        return CameraProjection.Orthographic;
      case 'perspective':
        return CameraProjection.Perspective;
      default:
        return undefined;
    }
  }

  private selectScandataFromQueryParams() {
    this.scandata$
      .pipe(
        filter((scans) => scans.length > 0),
        take(1),
        withLatestFrom(this.store.selectOnce(AppState.urlQueryScandataIds)),
        filter(([, scandataIds]) => scandataIds.length > 0),
        switchMap(([scans, scandataIds]) =>
          this.store.dispatch(new SelectOnly(scandataIds)).pipe(map(() => scans)),
        ),
        map((scans) => scans.filter((x) => x.selected)),
        filter((selected) => selected.length === 1),
        switchMap((selected) => this.host3dService.showScan(selected[0])),
        take(1),
      )
      .subscribe();
  }

  private async requestFocus() {
    const hasParams = this.store.selectSnapshot(AppState.hasUrlQueryParams);

    if (this.shouldRequestFocus || hasParams) {
      await this.workspace.api.extension.requestFocus().catch(() => false);
    }
  }

  private showScans(scans: ScandataModel[]) {
    const viewableScans = scans.filter((scan) => scan.pointCount > 0);

    // Ensure scans are loaded in the same order as they appear in the list.
    const sortInfo = this.store.selectSnapshot(ScandataState.sortInfo);
    const sortedScans = sortScandata(viewableScans, sortInfo.sortBy, sortInfo.sortDirection);

    const showScans$ = sortedScans.map((scan) => this.host3dService.showScan(scan).pipe(take(1)));

    concat(...showScans$).subscribe();
  }

  private async enablePickingTool() {
    try {
      // Workaround suggested by Connect.  Web3d update now disables picking tool
      // for some of the calls made during setCameraMode in Connect.
      await this.workspace.api.viewer.activateTool('reset');
    } catch (error: any) {
      this.logger.error(`Failed to enable picking tool`, {}, error);
    }
  }

  private subscribeToRotatePanoramas() {
    this.actions$
      .pipe(
        ofActionDispatched(RotatePanoramas),
        withLatestFrom(this.currentStation$),
        concatMap(([{ scanId, headingOffsetDegrees }, currentStation]) =>
          this.rotatePanoramas(scanId, headingOffsetDegrees, currentStation),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private rotatePanoramas(
    scanId: string,
    headingOffsetDegrees: number,
    currentStation?: CurrentStation,
  ) {
    const userRole = this.store.selectSnapshot(UserState.userRole);
    const saveTransform = userRole === Role.Admin || userRole === Role.FullAccess;

    return this.store.selectOnce(ScandataState.getScan(scanId)).pipe(
      map((scan) => {
        // Get the new station transform with the updated heading offset
        const stationTransform = getStationTransformHeading(
          scan?.stationTransform,
          headingOffsetDegrees,
        );
        return {
          originalStationTransform: scan?.stationTransform,
          newStationTransform: stationTransform,
        };
      }),
      switchMap((transforms) => {
        // Reload the station if it is currently displayed
        return currentStation?.station.pointcloudId === scanId
          ? from(this.reloadStation(currentStation!.station, transforms.newStationTransform)).pipe(
              map(() => transforms),
            )
          : of(transforms);
      }),
      switchMap((transforms) => {
        return saveTransform
          ? // Save the transform via API if the user has the right role
            this.host3dService.updateStationTransform(scanId, transforms.newStationTransform).pipe(
              catchError(() => {
                if (currentStation?.station.pointcloudId !== scanId) return EMPTY;
                return this.reloadStation(
                  currentStation!.station,
                  transforms.originalStationTransform,
                );
              }),
            )
          : // Otherwise, just patch the store
            this.store.dispatch(
              new PatchScandataModel({
                id: scanId,
                stationTransform: transforms.newStationTransform,
              }),
            );
      }),
    );
  }

  private createElevationPickingEffect() {
    effect(() => {
      const elevationPicking = this.elevationPicking();

      if (!isDefined(this.workspace)) return;

      if (elevationPicking) {
        this.activateElevationPickingTool();
      } else if (!elevationPicking) {
        this.workspace.api.viewer.activateTool('reset');
      }
    });
  }

  private async activateElevationPickingTool(): Promise<void> {
    return this.workspace.api.viewer.activateTool('picking', {
      snapTypes: ['point', 'edge', 'surface'],
      instruction: {
        title: this.translations.elevation.title.text,
        text: this.translations.elevation.usage.text,
        timeout: 5000,
      },
    });
  }

  private subscribeToElevationPicked() {
    this.workspace.event$
      .pipe(
        filter((event) => event.id === 'viewer.onPicked'),
        map((event) => event.data as PointPickedDetail),
        filter(
          (pickDetail): pickDetail is PointPickedDetail =>
            isDefined(pickDetail.position) && this.elevationPicking(),
        ),
        untilDestroyed(this),
      )
      .subscribe(async (pickDetail) => {
        this.store.dispatch(new ElevationPicked(pickDetail.position.z));
        this.elevationPicking.set(false);

        await this.removeElevationPointMarkup();

        await this.createElevationPointMarkup(
          new Vector3(
            pickDetail.position.x * this.markupToMeter,
            pickDetail.position.y * this.markupToMeter,
            pickDetail.position.z * this.markupToMeter,
          ),
        );
      });
  }

  private subscribeToElevationToolChanged() {
    this.workspace.event$
      .pipe(
        filter((event) => event.id === 'viewer.onToolChanged'),
        map((event) => event.data as ChangedViewerTool),
        filter((tool) => tool.old === 'picking' && tool.new !== 'picking'),
        filter(() => this.elevationPicking()),
        untilDestroyed(this),
      )
      .subscribe(() => this.elevationPicking.set(false));
  }

  private subscribeToRenderElevationMarkup() {
    this.elevationRenderingMode$
      .pipe(
        switchMap((elevationRenderingMode) =>
          elevationRenderingMode
            ? this.createElevationPointMarkup(
                this.elevationPointMarkup
                  ? new Vector3(
                      this.elevationPointMarkup.start.positionX,
                      this.elevationPointMarkup.start.positionY,
                      this.elevationPointMarkup.start.positionZ,
                    )
                  : undefined,
              )
            : this.removeElevationPointMarkup(),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private async removeElevationPointMarkup() {
    if (isDefined(this.elevationPointMarkup?.id)) {
      await this.workspace.api.markup.removeMarkups([this.elevationPointMarkup?.id]);
    }
  }

  private async createElevationPointMarkup(position?: Vector3) {
    if (isNil(position)) return;

    const markups = await this.workspace.api.markup.addSinglePointMarkups([
      {
        start: {
          positionX: position.x,
          positionY: position.y,
          positionZ: position.z,
        },
      },
    ]);

    this.elevationPointMarkup = markups.length > 0 ? markups[0] : undefined;
  }

  private async getTrimbimFileForClearance(): Promise<ConnectViewerFile | undefined> {
    // viewer.getTrimbimModels is not returning anything, so we need to iterate over the loaded models
    const loadedModels = await this.workspace.api.viewer.getModels();

    for (const model of loadedModels) {
      const loadedModel = await this.workspace.api.viewer.getLoadedModel(model.id);

      if (loadedModel?.file.runtimeType === 'TRIMBIM') {
        return loadedModel;
      }
    }

    return undefined;
  }

  private async setClearance(
    pointcloudId: string,
    trimbimFile: ConnectViewerFile,
    objectRuntimeIds: number[],
  ) {
    // Connect uses id of the model as reference, but the runtimeId is supplied to web3d
    // For normal Trimbim files, the runtimeId is the same as the id
    // For converted files like skp and ifc, the runtimeId is the versionId of the converted file
    const trimbimModelId = trimbimFile.file.runtimeId ?? trimbimFile.id;

    // Seems that getHierarchyChildren can use runtimeId or id as reference
    const hierarchyObjects = await this.workspace.api.viewer.getHierarchyChildren(
      trimbimModelId,
      objectRuntimeIds,
      undefined,
      true,
    );

    const hierarchyObjectIds = hierarchyObjects.map((o) => o.id);

    // This method uses the runtimeId for the trimbim model
    await this.workspace.api.viewer.setPointCloudClearance(
      pointcloudId,
      trimbimModelId,
      [...objectRuntimeIds, ...hierarchyObjectIds],
      this.hasClearanceWebGPUFeature(),
    );
  }
}
